import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import {
  CreatePspToWalletDepositTransaction,
  denormalizeCreatePspToWalletDepositTransaction,
} from '@model/CreatePspToWalletDepositTransaction'
import {
  MasterTransaction,
  isTransactionFormRedirect,
  isTransactionHistory,
  isTransactionSuccessful,
  isTransactionUnSuccessful,
  isTransactionUrlRedirect,
} from '@model/MasterTransaction'
import { isTransactionRedirectStaticType } from '@model/MasterTransactionDto'
import { isPaymentProviderBankType } from '@model/PaymentProviderType'
import type { WalletDepositFormValues } from '@model/WalletDepositFormValues'

interface WalletDepositMutateProps {
  onSubmit(data: MasterTransaction, values: WalletDepositFormValues): void
  onRedirect?(data: MasterTransaction, values: WalletDepositFormValues): void
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useWalletDepositMutate = (props: WalletDepositMutateProps) => {
  const { onSubmit, onRedirect } = props

  const locale = useSessionLanguage()
  const queryClient = useQueryClient()

  const apiClient = useApiClient(ClientApiClient)

  const handleSuccess = async (data: MasterTransaction, values: WalletDepositFormValues) => {
    if (isTransactionHistory(data)) {
      onSubmit(data, values)
    } else if (isPaymentProviderBankType(values.paymentProvider.providerCategory.id, data)) {
      onSubmit(data, values)
    } else if (isTransactionUrlRedirect(data) || isTransactionFormRedirect(data)) {
      if (onRedirect) {
        await onRedirect(data, values)
      } else {
        onSubmit(data, values)
      }
    } else if (isTransactionRedirectStaticType(data)) {
      onSubmit(data, values)
    } else if (isTransactionSuccessful(data)) {
      onSubmit(data, values)
    } else if (isTransactionUnSuccessful(data)) {
      onSubmit(data, values)
    }
  }

  const handleError = () => {
    // TODO: add error notification
  }

  return useMutation({
    mutationFn: async (values: WalletDepositFormValues) => {
      return apiClient.transactionDeposit(
        denormalizeCreatePspToWalletDepositTransaction(
          createPspToWalletDepositTransaction(values),
          locale
        )
      )
    },
    onSuccess: (data, values) => {
      queryClient.invalidateQueries({ queryKey: ['wallet'] })
      queryClient.invalidateQueries({ queryKey: ['transaction'] })
      queryClient.invalidateQueries({ queryKey: ['tradingAccount'] })
      handleSuccess(data, values)
    },
    onError: handleError,
    retry: false,
  })
}

const createPspToWalletDepositTransaction = (
  values: WalletDepositFormValues
): CreatePspToWalletDepositTransaction => {
  return {
    fromPaymentProvider: {
      id: values.paymentProvider.id,
      currency: {
        id: values.paymentProvider.currency.id,
      },
    },
    toWallet: {
      id: values.wallet.id,
      currency: {
        id: values.wallet.currency.id,
      },
    },
    amount: values.amount,
    comment: values.comment,
    terms: values.terms.paymentOperations,
    form: values.paymentProvider.parameters.form,
    fields: values.fields,
    bonusCampaignId: values.bonusCampaignId,
  }
}
